import { type IGeoreference } from "@interfaces/common/IGeoreference";
import { type ISettings } from "@interfaces/common/ISettings";
import { type ITypeState } from "@interfaces/common/ITypeState";
import { type IAccessibleModule } from "@interfaces/modules/IAccessibleModule";
import {
  Action,
  createSelector,
  Selector,
  State,
  type StateContext,
} from "@ngxs/store";
import { ModulesService } from "@services/modules.service";
import { tap } from "rxjs";

import { inject, Injectable } from "@angular/core";

import {
  ClearAccessibleModules,
  ClearCompanyGeoreference,
  ClearSavedRut,
  SetAccessibleModules,
  SetCompanyGeoreference,
  SetCurrentTenant,
  SetCurrentTenantBusinessName,
  SetSavedRut,
  SetSettings,
  SetTypeState,
} from "./common.action";

export interface CommonStateModel {
  version: string;
  typeStates: ITypeState[];
  settings: ISettings | null;
  currentTenant: string | null;
  currentTenantBusinessName: string | null;
  savedRut: string | null;
  accessibleModules: IAccessibleModule[];
  companyGeoreference: IGeoreference | null;
}

@State<CommonStateModel>({
  name: "Common",
  defaults: {
    version: "1.0.0",
    typeStates: [],
    settings: null,
    currentTenant: null,
    currentTenantBusinessName: null,
    savedRut: null,
    accessibleModules: [],
    companyGeoreference: null,
  },
})
@Injectable()
export class CommonState {
  private modulesService = inject(ModulesService);

  @Selector()
  static version(state: CommonStateModel) {
    return state.version;
  }

  @Selector()
  static typeStates(state: CommonStateModel) {
    return state.typeStates;
  }

  @Selector()
  static settings(state: CommonStateModel) {
    return state.settings;
  }

  @Selector()
  static currentTenant(state: CommonStateModel) {
    return state.currentTenant;
  }

  @Selector()
  static currentTenantBusinessName(state: CommonStateModel) {
    return state.currentTenantBusinessName;
  }

  @Selector()
  static savedRut(state: CommonStateModel) {
    return state.savedRut;
  }

  @Selector()
  static accessibleModules(state: CommonStateModel) {
    return state.accessibleModules;
  }

  @Selector()
  static accessibleModuleNames(state: CommonStateModel): Set<string> {
    return new Set(state.accessibleModules.map(m => m.name));
  }

  @Selector()
  static accessibleRouteKeys(state: CommonStateModel): Set<string> {
    return new Set(state.accessibleModules.map(m => m.routeKey));
  }

  @Selector()
  static companyGeoreference(state: CommonStateModel) {
    return state.companyGeoreference;
  }

  static getTypeStateByDescription(description: string) {
    return createSelector([CommonState], (state: CommonStateModel) =>
      state.typeStates.find(typeState => typeState.description === description)
    );
  }

  @Action(SetTypeState)
  setTypeState(ctx: StateContext<CommonStateModel>, action: SetTypeState) {
    ctx.patchState({
      typeStates: action.typeStates,
    });
  }

  @Action(SetSettings)
  setSettings(ctx: StateContext<CommonStateModel>, action: SetSettings) {
    ctx.patchState({
      settings: action.settings,
    });
  }

  @Action(SetCurrentTenant)
  setCurrentTenant(
    ctx: StateContext<CommonStateModel>,
    action: SetCurrentTenant
  ) {
    ctx.patchState({
      currentTenant: action.currentTenant,
    });
  }

  @Action(SetSavedRut)
  setSavedRut(ctx: StateContext<CommonStateModel>, action: SetSavedRut) {
    ctx.patchState({
      savedRut: action.rut,
    });
  }

  @Action(ClearSavedRut)
  clearSavedRut(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      savedRut: null,
    });
  }

  @Action(SetCurrentTenantBusinessName)
  setCurrentTenantBusinessName(
    ctx: StateContext<CommonStateModel>,
    action: SetCurrentTenantBusinessName
  ) {
    ctx.patchState({
      currentTenantBusinessName: action.businessName,
    });
  }

  @Action(SetAccessibleModules)
  setAccessibleModules(
    ctx: StateContext<CommonStateModel>,
    action: SetAccessibleModules
  ) {
    const tenant = action.tenant ?? ctx.getState().currentTenant;

    if (!tenant) {
      return;
    }

    return this.modulesService.getUserAccessibleModules(tenant).pipe(
      tap({
        next: res => {
          ctx.patchState({
            accessibleModules: res.data,
          });
        },
        error: err => {
          console.error("[SetAccessibleModules] Error fetching modules:", err);
          ctx.patchState({
            accessibleModules: [],
          });
        },
      })
    );
  }

  @Action(ClearAccessibleModules)
  clearAccessibleModules(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      accessibleModules: [],
    });
  }

  @Action(SetCompanyGeoreference)
  setCompanyGeoreference(
    ctx: StateContext<CommonStateModel>,
    action: SetCompanyGeoreference
  ) {
    ctx.patchState({
      companyGeoreference: action.companyGeoreference,
    });
  }

  @Action(ClearCompanyGeoreference)
  clearCompanyGeoreference(ctx: StateContext<CommonStateModel>) {
    ctx.patchState({
      companyGeoreference: null,
    });
  }
}
